import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { staggerContainer, staggerItem, viewportConfig } from './AnimationVariants';

const socialIcons = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61589360863116',
    svg: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/woodlandscentrefordentistry',
    svg: <>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </>,
  },
  {
    label: 'Twitter',
    href: 'https://www.twitter.com',
    svg: <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@woodlandscentre4dentistry',
    svg: <>
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
      <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
    </>,
  },
];

const hours = [
  { day: 'Monday – Friday', time: '9:00 AM – 6:00 PM' },
  { day: 'Saturday – Sunday', time: 'Closed' },
];

export default function Footer() {
  return (
    <footer className="bg-forest-dark text-cream">
      <div className="overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" className="w-full" preserveAspectRatio="none" style={{ display: 'block' }}>
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,0 L0,0 Z" fill="#FFF8F0" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-8 pb-12">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
        >
          {/* Brand — spans 2 columns */}
          <motion.div variants={staggerItem} className="lg:col-span-2">
            <div className="max-w-sm">
              <img
                src={`${process.env.PUBLIC_URL}/logo-tranparent.png`}
                alt="Woodlands Centre for Dentistry"
                style={{ height: '5.5rem', display: 'block' }}
              />
              <p className="text-cream/70 text-sm leading-relaxed mt-4 mb-5">
                Where nature meets your smile. A dental practice inspired by the serenity of the forest — because your comfort matters as much as your care.
              </p>
              <div className="flex gap-3">
                {socialIcons.map(({ label, href, svg }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="w-9 h-9 rounded-full bg-forest border border-sage/30 flex items-center justify-center"
                    whileHover={{ scale: 1.15, backgroundColor: '#C5A55A', borderColor: '#C5A55A' }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cream">
                      {svg}
                    </svg>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div variants={staggerItem}>
            <h4 className="font-serif font-semibold text-white text-lg mb-5">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-cream/70 text-sm">106 Woodlands<br />North Harrow, HA2 6EW</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                <a href="tel:+442088665556" className="text-cream/70 hover:text-gold text-sm transition-colors">020 8866 5556</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                <a href="mailto:hello@wc4d.co.uk" className="text-cream/70 hover:text-gold text-sm transition-colors">hello@wc4d.co.uk</a>
              </li>
            </ul>
          </motion.div>

          {/* Hours */}
          <motion.div variants={staggerItem}>
            <h4 className="font-serif font-semibold text-white text-lg mb-5">Office Hours</h4>
            <ul className="space-y-3">
              {hours.map((h) => (
                <li key={h.day} className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-cream text-sm font-medium">{h.day}</div>
                    <div className="text-cream/60 text-xs">{h.time}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-5 p-3 bg-forest rounded-xl border border-sage/20">
              <p className="text-xs text-cream/80">
                <span className="text-gold font-semibold">Emergency?</span> Call us anytime at{' '}
                <a href="tel:+442088665556" className="text-gold hover:underline">020 8866 5556</a>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="border-t border-sage/20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4 pb-2 flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-1">
          <p className="text-cream/50 text-xs">© {new Date().getFullYear()} Woodlands Dentistry. All rights reserved.</p>
          <p className="text-cream/40 text-xs italic md:mr-[83px]">Rooted in care. Growing with you.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-cream/50 hover:text-cream text-xs transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-cream/50 hover:text-cream text-xs transition-colors">Terms of Service</Link>
          </div>
        </div>
        <div className="text-center pb-3">
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
            Designed &amp; Developed by{' '}
            <a href="https://azulogic.in" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', transition: 'opacity 0.2s' }}
              onMouseOver={e => e.currentTarget.style.opacity = '0.8'}
              onMouseOut={e => e.currentTarget.style.opacity = '1'}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.85rem', fontWeight: 500 }}>
                <span style={{ color: '#ffffff' }}><b>azu</b></span><span style={{ color: '#AFA9EC' }}><b>logic</b></span>
              </span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
